import { usePageRefresh } from '../../../../context/PageRefreshContext';
import { localDateString } from '../../../../utils/helpers/localDate';
import { useState, useEffect, useCallback } from 'react';
import { Icon } from '@iconify/react';
import { useDispatch, useSelector } from 'react-redux';
import { getInvoiceAgingReport } from '../../../../redux/actions/SocietyPortal/InvoiceReportAction/InvoiceReportAction';
import { getInvoiceTypes } from '../../../../redux/actions/SocietyPortal/InvoiceTypeAction/InvoiceTypeAction';
import { getWingsDropdown, getFlatsDropdown } from '../../../../redux/actions/WingsAndFlatsActions/WingsAndFlatsAction';
import { exportInvoiceAgingReportReq } from '../../../../axios/request/SocietyPortal/InvoiceReportReqs/InvoiceReportReqs';
import { selectedSocietyStorer } from '../../../../utils/webStorer';
import { PaginationLimit } from '../../../../components/PaginationLimit';
import { formatIstDate } from '../../../../utils/helpers/istDate';

const AGING_BUCKETS = ['0-30', '31-60', '61-90', '91-180', '180+'];

const BUCKET_CONFIG = {
    '0-30':   { label: '0-30 Days',   color: 'tw-bg-emerald-50 tw-border-emerald-200', text: 'tw-text-emerald-700', dot: 'tw-bg-emerald-500' },
    '31-60':  { label: '31-60 Days',  color: 'tw-bg-yellow-50 tw-border-yellow-200',   text: 'tw-text-yellow-700',  dot: 'tw-bg-yellow-500' },
    '61-90':  { label: '61-90 Days',  color: 'tw-bg-orange-50 tw-border-orange-200',   text: 'tw-text-orange-700',  dot: 'tw-bg-orange-500' },
    '91-180': { label: '91-180 Days', color: 'tw-bg-red-50 tw-border-red-200',         text: 'tw-text-red-700',     dot: 'tw-bg-red-500' },
    '180+':   { label: '180+ Days',   color: 'tw-bg-red-100 tw-border-red-300',        text: 'tw-text-red-900',     dot: 'tw-bg-red-800' },
};

const STATUS_OPTIONS = [
    { label: 'All', value: 'ALL' },
    { label: 'Unpaid', value: 'UNPAID' },
    { label: 'Partial', value: 'PARTIAL' },
];

const InvoiceAgingReport = () => {
    const dispatch = useDispatch();
    const societyId = selectedSocietyStorer.getSocietyId();

    const { agingData, agingSummary, agingPagination, agingLoading } = useSelector(state => state.InvoiceReportReducer || {});
    const { invoiceTypesData } = useSelector(state => state.InvoiceTypeReducers || {});
    const { wingsDropdown, flatsDropdown } = useSelector(state => state.WingsAndFlatsReducers || {});

    const [filters, setFilters] = useState({
        invoice_type_id: '',
        wing_id: '',
        flat_id: '',
        as_on_date: localDateString(),
        aging_bucket: 'ALL',
        status_filter: 'ALL',
        search: '',
        sort_by: 'age',
        sort_order: 'DESC',
    });
    const [page, setPage] = useState(1);
    const [limit, setLimit] = useState(20);
    const [exporting, setExporting] = useState(false);

    useEffect(() => {
        dispatch(getInvoiceTypes({ society_id: societyId, page: 1, limit: 100 }));
        dispatch(getWingsDropdown({ society_id: societyId }));
    }, [dispatch, societyId]);

    useEffect(() => {
        if (filters.wing_id) {
            dispatch(getFlatsDropdown({ wing_id: filters.wing_id }));
        }
    }, [dispatch, filters.wing_id]);

    const fetchReport = useCallback(() => {
        const params = { society_id: societyId, page, limit };
        if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
        if (filters.wing_id) params.wing_id = filters.wing_id;
        if (filters.flat_id) params.flat_id = filters.flat_id;
        if (filters.as_on_date) params.as_on_date = filters.as_on_date;
        if (filters.aging_bucket && filters.aging_bucket !== 'ALL') params.aging_bucket = filters.aging_bucket;
        if (filters.status_filter && filters.status_filter !== 'ALL') params.status_filter = filters.status_filter;
        if (filters.search) params.search = filters.search;
        if (filters.sort_by) params.sort_by = filters.sort_by;
        if (filters.sort_order) params.sort_order = filters.sort_order;
        dispatch(getInvoiceAgingReport(params));
    }, [dispatch, societyId, filters, page, limit]);
    usePageRefresh(fetchReport);

    useEffect(() => {
        fetchReport();
    }, [fetchReport]);

    const handleFilterChange = (name, value) => {
        setFilters(prev => {
            const updated = { ...prev, [name]: value };
            if (name === 'wing_id') updated.flat_id = '';
            return updated;
        });
        setPage(1);
    };

    const handleBucketClick = (bucket) => {
        handleFilterChange('aging_bucket', filters.aging_bucket === bucket ? 'ALL' : bucket);
    };

    const handleSort = (field) => {
        setFilters(prev => ({
            ...prev,
            sort_by: field,
            sort_order: prev.sort_by === field && prev.sort_order === 'ASC' ? 'DESC' : 'ASC',
        }));
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const params = { society_id: societyId };
            if (filters.invoice_type_id) params.invoice_type_id = filters.invoice_type_id;
            if (filters.wing_id) params.wing_id = filters.wing_id;
            if (filters.flat_id) params.flat_id = filters.flat_id;
            if (filters.as_on_date) params.as_on_date = filters.as_on_date;
            if (filters.aging_bucket && filters.aging_bucket !== 'ALL') params.aging_bucket = filters.aging_bucket;
            if (filters.status_filter && filters.status_filter !== 'ALL') params.status_filter = filters.status_filter;
            if (filters.search) params.search = filters.search;

            const response = await exportInvoiceAgingReportReq(params);
            const blob = new Blob([response], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'invoice_aging_report.xlsx';
            a.click();
            window.URL.revokeObjectURL(url);
        } catch (e) {
            console.error('Export failed:', e);
        } finally {
            setExporting(false);
        }
    };

    const handleReset = () => {
        setFilters({
            invoice_type_id: '',
            wing_id: '',
            flat_id: '',
            as_on_date: localDateString(),
            aging_bucket: 'ALL',
            status_filter: 'ALL',
            search: '',
            sort_by: 'age',
            sort_order: 'DESC',
        });
        setPage(1);
    };

    const invoices = agingData || [];
    const summary = agingSummary || {};
    const pagination = agingPagination || {};
    const wings = wingsDropdown || [];
    const flats = Array.isArray(flatsDropdown) ? flatsDropdown : flatsDropdown?.data || [];
    const invoiceTypes = invoiceTypesData || [];

    const formatCurrency = (amount) => {
        return parseFloat(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };

    const formatDate = (dateStr) => {
        if (!dateStr) return '-';
        const d = new Date(dateStr);
        return formatIstDate(d);
    };

    const getAgeColor = (ageDays) => {
        if (ageDays <= 30) return 'tw-text-emerald-600';
        if (ageDays <= 60) return 'tw-text-yellow-600';
        if (ageDays <= 90) return 'tw-text-orange-500';
        if (ageDays <= 180) return 'tw-text-red-500';
        return 'tw-text-red-800 tw-font-bold';
    };

    const getStatusBadge = (status) => {
        const styles = {
            Unpaid: 'tw-bg-red-50 tw-text-red-700',
            Partial: 'tw-bg-amber-50 tw-text-amber-700',
        };
        return (
            <span className={`tw-inline-flex tw-items-center tw-px-2 tw-py-0.5 tw-rounded tw-text-[11px] tw-font-medium ${styles[status] || 'tw-bg-gray-50 tw-text-gray-600'}`}>
                {status}
            </span>
        );
    };

    const grandTotal = AGING_BUCKETS.reduce((acc, b) => ({
        count: acc.count + (summary[b]?.count || 0),
        total_amount: acc.total_amount + (summary[b]?.total_amount || 0),
        total_balance: acc.total_balance + (summary[b]?.total_balance || 0),
    }), { count: 0, total_amount: 0, total_balance: 0 });

    const getSortIcon = (field) => {
        if (filters.sort_by !== field) return 'mdi:unfold-more-horizontal';
        return filters.sort_order === 'ASC' ? 'mdi:arrow-up' : 'mdi:arrow-down';
    };

    const columns = [
        { header: 'Sr No', key: 'index', align: 'center', width: '50px' },
        { header: 'Date', key: 'bill_date', sortKey: 'bill_date' },
        { header: 'Invoice No', key: 'bill_no' },
        { header: 'Wing', key: 'wing' },
        { header: 'Unit No', key: 'unit_no', sortKey: 'unit_no' },
        { header: 'Member Name', key: 'member_name' },
        { header: 'Invoice Type', key: 'invoice_type' },
        { header: 'Age (Days)', key: 'age_days', align: 'right', sortKey: 'age' },
        { header: 'Amount', key: 'amount', align: 'right', sortKey: 'amount' },
        { header: 'Balance', key: 'balance', align: 'right', sortKey: 'balance' },
        { header: 'Status', key: 'status', align: 'center' },
    ];

    return (
        <>
            {/* Header */}
            <div className="tw-flex tw-items-start tw-justify-between tw-mb-4">
                <div>
                    <h1 className="tw-text-lg tw-font-bold tw-text-gray-800">Invoice Aging Report</h1>
                    <p className="tw-text-xs tw-text-gray-400 tw-mt-0.5">Outstanding invoices by aging period</p>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    <button
                        onClick={handleExport}
                        disabled={exporting || agingLoading}
                        className="tw-flex tw-items-center tw-gap-1.5 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-600 tw-border tw-border-gray-200 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors disabled:tw-opacity-50"
                    >
                        {exporting ? (
                            <Icon icon="mdi:loading" className="tw-text-sm tw-animate-spin" />
                        ) : (
                            <Icon icon="mdi:file-excel-outline" className="tw-text-sm" />
                        )}
                        {exporting ? 'Exporting...' : 'Export Excel'}
                    </button>
                </div>
            </div>

            {/* Aging Summary Cards */}
            <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3 tw-mb-4">
                {AGING_BUCKETS.map(bucket => {
                    const config = BUCKET_CONFIG[bucket];
                    const data = summary[bucket] || { count: 0, total_balance: 0 };
                    const isActive = filters.aging_bucket === bucket;
                    return (
                        <button
                            key={bucket}
                            onClick={() => handleBucketClick(bucket)}
                            className={`tw-rounded-lg tw-border tw-p-3 tw-text-left tw-transition-all ${config.color} ${isActive ? 'tw-ring-2 tw-ring-blue-500 tw-ring-offset-1' : ''}`}
                        >
                            <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
                                <div className={`tw-w-2 tw-h-2 tw-rounded-full ${config.dot}`}></div>
                                <span className={`tw-text-[11px] tw-font-semibold ${config.text}`}>{config.label}</span>
                            </div>
                            <div className={`tw-text-base tw-font-bold ${config.text}`}>
                                {formatCurrency(data.total_balance)}
                            </div>
                            <div className="tw-text-[10px] tw-text-gray-500">{data.count} invoice(s)</div>
                        </button>
                    );
                })}
                {/* Grand Total */}
                <button
                    onClick={() => handleFilterChange('aging_bucket', 'ALL')}
                    className={`tw-rounded-lg tw-border tw-p-3 tw-text-left tw-transition-all tw-bg-blue-50 tw-border-blue-200 ${filters.aging_bucket === 'ALL' ? 'tw-ring-2 tw-ring-blue-500 tw-ring-offset-1' : ''}`}
                >
                    <div className="tw-flex tw-items-center tw-gap-1.5 tw-mb-1.5">
                        <div className="tw-w-2 tw-h-2 tw-rounded-full tw-bg-blue-600"></div>
                        <span className="tw-text-[11px] tw-font-semibold tw-text-blue-700">Total</span>
                    </div>
                    <div className="tw-text-base tw-font-bold tw-text-blue-700">
                        {formatCurrency(grandTotal.total_balance)}
                    </div>
                    <div className="tw-text-[10px] tw-text-gray-500">{grandTotal.count} invoice(s)</div>
                </button>
            </div>

            {/* Filters */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-p-4 tw-mb-4">
                <div className="tw-grid tw-grid-cols-2 md:tw-grid-cols-3 lg:tw-grid-cols-6 tw-gap-3">
                    {/* Invoice Type */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Invoice Type</label>
                        <select
                            value={filters.invoice_type_id}
                            onChange={(e) => handleFilterChange('invoice_type_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            <option value="">All Types</option>
                            {invoiceTypes.map(t => (
                                <option key={t.invoice_type_id} value={t.invoice_type_id}>{t.invoice_title}</option>
                            ))}
                        </select>
                    </div>

                    {/* Wing */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Wing</label>
                        <select
                            value={filters.wing_id}
                            onChange={(e) => handleFilterChange('wing_id', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            <option value="">All Wings</option>
                            {wings.map(w => (
                                <option key={w.wing_id} value={w.wing_id}>{w.title}</option>
                            ))}
                        </select>
                    </div>

                    {/* Flat */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Flat</label>
                        <select
                            value={filters.flat_id}
                            onChange={(e) => handleFilterChange('flat_id', e.target.value)}
                            disabled={!filters.wing_id}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500 disabled:tw-bg-gray-50 disabled:tw-text-gray-400"
                        >
                            <option value="">All Flats</option>
                            {flats.map(f => (
                                <option key={f.flat_id} value={f.flat_id}>{f.flat_number || f.flat_name}</option>
                            ))}
                        </select>
                    </div>

                    {/* As On Date */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">As On Date</label>
                        <input
                            type="date"
                            value={filters.as_on_date}
                            onChange={(e) => handleFilterChange('as_on_date', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        />
                    </div>

                    {/* Status */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Status</label>
                        <select
                            value={filters.status_filter}
                            onChange={(e) => handleFilterChange('status_filter', e.target.value)}
                            className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-px-2.5 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                        >
                            {STATUS_OPTIONS.map(o => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                            ))}
                        </select>
                    </div>

                    {/* Search */}
                    <div>
                        <label className="tw-block tw-text-[11px] tw-font-medium tw-text-gray-500 tw-mb-1">Search</label>
                        <div className="tw-relative">
                            <Icon icon="mdi:magnify" className="tw-absolute tw-left-2.5 tw-top-1/2 tw--translate-y-1/2 tw-text-gray-400 tw-text-sm" />
                            <input
                                type="text"
                                placeholder="Bill no or member..."
                                value={filters.search}
                                onChange={(e) => handleFilterChange('search', e.target.value)}
                                className="tw-w-full tw-border tw-border-gray-300 tw-rounded-md tw-pl-8 tw-pr-3 tw-py-1.5 tw-text-xs focus:tw-outline-none focus:tw-ring-1 focus:tw-ring-blue-500"
                            />
                        </div>
                    </div>
                </div>

                <div className="tw-flex tw-justify-end tw-mt-3">
                    <button
                        onClick={handleReset}
                        className="tw-flex tw-items-center tw-gap-1 tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-gray-500 tw-border tw-border-gray-200 tw-rounded-md hover:tw-bg-gray-50"
                    >
                        <Icon icon="mdi:refresh" className="tw-text-sm" />
                        Reset
                    </button>
                </div>
            </div>

            {/* Table */}
            <div className="tw-bg-white tw-rounded-lg tw-border tw-border-gray-200 tw-overflow-hidden">
                <div className="tw-overflow-x-auto">
                    <table className="tw-w-full tw-text-xs">
                        <thead>
                            <tr className="tw-bg-gray-50 tw-border-b tw-border-gray-200">
                                {columns.map(col => (
                                    <th
                                        key={col.key}
                                        className={`tw-px-3 tw-py-2.5 tw-text-[11px] tw-font-semibold tw-text-gray-600 tw-uppercase tw-tracking-wider tw-whitespace-nowrap ${col.sortKey ? 'tw-cursor-pointer hover:tw-bg-gray-100' : ''}`}
                                        style={{ textAlign: col.align || 'left', minWidth: col.width || 'auto' }}
                                        onClick={() => col.sortKey && handleSort(col.sortKey)}
                                    >
                                        <span className="tw-inline-flex tw-items-center tw-gap-1">
                                            {col.header}
                                            {col.sortKey && (
                                                <Icon icon={getSortIcon(col.sortKey)} className="tw-text-xs tw-text-gray-400" />
                                            )}
                                        </span>
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {agingLoading ? (
                                <tr>
                                    <td colSpan={columns.length} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:loading" className="tw-animate-spin tw-text-2xl tw-text-gray-300 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">Loading report...</p>
                                    </td>
                                </tr>
                            ) : invoices.length === 0 ? (
                                <tr>
                                    <td colSpan={columns.length} className="tw-text-center tw-py-12">
                                        <Icon icon="mdi:file-document-outline" className="tw-text-4xl tw-text-gray-200 tw-mx-auto" />
                                        <p className="tw-text-xs tw-text-gray-400 tw-mt-2">No outstanding invoices found</p>
                                    </td>
                                </tr>
                            ) : (
                                invoices.map((inv, idx) => (
                                    <tr key={inv.invoice_id} className="tw-border-b tw-border-gray-100 hover:tw-bg-gray-50/50">
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-text-gray-500">{(page - 1) * limit + idx + 1}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{formatDate(inv.bill_date)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700 tw-font-medium">{inv.bill_no || '-'}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{inv.wing}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{inv.unit_no}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-700">{inv.member_name}</td>
                                        <td className="tw-px-3 tw-py-2 tw-whitespace-nowrap tw-text-gray-500">{inv.invoice_type}</td>
                                        <td className={`tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-medium ${getAgeColor(inv.age_days)}`}>
                                            {inv.age_days}
                                        </td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-text-gray-700">{formatCurrency(inv.amount)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-right tw-whitespace-nowrap tw-font-medium tw-text-gray-800">{formatCurrency(inv.balance)}</td>
                                        <td className="tw-px-3 tw-py-2 tw-text-center tw-whitespace-nowrap">{getStatusBadge(inv.status)}</td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination */}
            {pagination.total > 0 && (
                <PaginationLimit
                    currentPage={page}
                    totalRecords={pagination.total || 0}
                    limit={limit}
                    handleLimitChange={(e) => { setLimit(Number(e.target.value)); setPage(1); }}
                    handlePageChange={(p) => setPage(p)}
                />
            )}
        </>
    );
};

export default InvoiceAgingReport;
